import { Tab, Tabs, Theme, Typography } from "@outschool/backpack";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useIsMobile, useScrollToElement } from "@outschool/ui-utils";
import React, { useState } from "react";

export interface TabConfig {
  label: string;
  value: string;
  ref: React.RefObject<HTMLElement> | React.RefObject<HTMLElement>[];
}

function ClassDetailsNav({ tabs }: { tabs: TabConfig[] }) {
  const isMobile = useIsMobile();
  const [selectedTab, setSelectedTab] = useState(tabs?.[0]?.value ?? undefined);
  const trackEvent = useTrackEvent();
  const scrollToElement = useScrollToElement({ desktopOffset: 60 });
  const scrollToSection = React.useCallback(
    (value: string) => {
      const tab = tabs.find(tab => tab.value === value);
      // no action if tab not found
      if (!tab) {
        console.warn(`Tab ${value} not found.  All tabs: `, tabs);
        return;
      }
      const ref = Array.isArray(tab.ref)
        ? tab.ref.find(r => !!r.current)?.current
        : tab.ref.current;
      if (ref) {
        scrollToElement(ref);
      }
    },
    [scrollToElement, tabs]
  );

  const topIntersectingSection = useIsIntersecting({ tabs });
  React.useEffect(() => {
    if (topIntersectingSection) {
      setSelectedTab(topIntersectingSection);
    }
  }, [topIntersectingSection]);

  const handleOnTabChange = (e: React.SyntheticEvent, value: string) => {
    trackEvent(`class_details_nav_bar_touch`, {
      selectedTab: value,
      currentTab: selectedTab,
    });
    setSelectedTab(value);
    scrollToSection(value);
  };

  // HACK(ish?): If don't have a valid tab to scroll to don't render it (yet)
  const tabsWithValidRefs = tabs.filter(tab =>
    Array.isArray(tab.ref)
      ? tab.ref.length > 0 && tab.ref.some(r => !!r.current)
      : !!tab.ref.current
  );
  if (tabsWithValidRefs.length === 0) {
    return null;
  }

  return (
    <Tabs
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 200,
        backgroundColor: "white",
        borderBottom: "1px solid",
        borderBottomColor: "neutral.200",
        "& .MuiTabs-indicator": {
          height: "5px",
        },
        height: 60,
        marginTop: 32,
        marginBottom: 32,
      }}
      onChange={handleOnTabChange}
      value={selectedTab}
    >
      {tabsWithValidRefs.map(tab => (
        <Tab
          component={"a"}
          href={`#${tab.value}`}
          key={tab.value}
          value={tab.value}
          sx={(theme: Theme) => ({
            padding: 0,
            marginRight: isMobile ? 8 : "1.5em",
            alignItems: "flex-start",
            minWidth: 0,
            "&:hover": {
              color: "primary.main",
            },
            marginLeft: isMobile ? 16 : 0,
            [theme.breakpoints.down("lg")]: {
              marginRight: isMobile ? 8 : "1.2em",
            },
          })}
          label={
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {tab.label}
            </Typography>
          }
        />
      ))}
    </Tabs>
  );
}

function useIsIntersecting({ tabs }: { tabs: TabConfig[] }) {
  const [topIntersectingSection, setTopIntersectingSection] = useState<
    string | undefined
  >();

  React.useEffect(() => {
    // Map of tab value to which indicies of refs are intersecting
    // This tells us the overall state of intersections among tabs
    // We use this logic to determine which tab to highlight
    // if a tab has one or more intersections: allIntersections.current[tab.value].size > 0
    // if a tab has no intersections: allIntersections.current[tab.value].size === 0
    const allIntersections = new Map<string, Set<number>>(
      tabs.map(t => [t.value, new Set<number>()])
    );

    const observer = new IntersectionObserver(
      (entries, _observer) => {
        for (const entry of entries) {
          let tab: TabConfig | undefined = undefined;
          let refIndex = -1;
          for (const t of tabs) {
            if (Array.isArray(t.ref)) {
              refIndex = t.ref.findIndex(r => r.current === entry.target);
              if (refIndex !== -1) {
                tab = t;
                break;
              }
            } else if (t.ref.current === entry.target) {
              tab = t;
              refIndex = 0;
              break;
            }
          }

          if (!tab) {
            console.warn(
              "Could not find tab for IntersectionObserver callback: ",
              entry
            );
            continue;
          }

          const alreadyIntersecting = allIntersections.get(tab.value);
          if (!alreadyIntersecting) {
            console.warn(`${tab.value} has no intersection set?`);
            continue;
          }

          if (entry.isIntersecting) {
            alreadyIntersecting.add(refIndex);
          } else {
            alreadyIntersecting.delete(refIndex);
          }
        }

        // find top intersection now
        for (const tab of tabs) {
          const nInteresections = allIntersections.get(tab.value)?.size ?? 0;
          if (nInteresections > 0) {
            setTopIntersectingSection(tab.value);
            return;
          }
        }
        // did not short circuit above, so there is no intersection found.
        // reset state to "none intersecting"
        setTopIntersectingSection(undefined);
      },
      {
        rootMargin: "-50px 0px",
      }
    );

    for (const tab of tabs) {
      if (Array.isArray(tab.ref)) {
        for (const ref of tab.ref) {
          if (ref.current) {
            observer.observe(ref.current);
          }
        }
      } else if (tab.ref.current) {
        observer.observe(tab.ref.current);
      }
    }

    return () => {
      try {
        observer.disconnect();
      } catch (e) {
        // do nothing
      }
    };
  }, [tabs]);

  return topIntersectingSection;
}

export default React.memo(ClassDetailsNav);
